import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Music, Link2, Check, Send, X } from 'lucide-react';

// One-time announcement of the Song Approval feature, shown to editors.
//
// This one must be ACKNOWLEDGED, not snoozed — unlike the client must-haves
// intro, there's no dismiss, no Escape, no backdrop click and no close button.
// An editor who never reads it simply never uses the feature, so it keeps
// returning until the box is ticked. The ack is stored on the user record
// (song_intro.acknowledged) rather than in browser storage, so it follows them
// across devices and a cleared cache doesn't nag them again.
//
// The preview below is built from real markup rather than a screenshot: a PNG
// would drift out of date the first time the panel changes, and this stays
// theme-aware for free.

const SESSION_KEY = 'ug_song_intro_seen_this_session';

export default function SongApprovalIntro() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'editor') return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    let timer;
    api.get('/editor/song-approval-intro')
      .then(r => {
        if (!r.data?.eligible) return;
        // Wait our turn behind the time survey, a PROMPT at z-210 (EditorTimeSurvey.js), rather
        // than stack two dialogs on an editor. One beat before the first look, because the survey
        // mounts from its own fetch; sessionStorage is written only when we actually open, so
        // waiting can never swallow the announcement.
        const showWhenClear = () => {
          if (document.querySelector('[data-testid="time-survey-title"]')) {
            timer = setTimeout(showWhenClear, 800);
            return;
          }
          sessionStorage.setItem(SESSION_KEY, '1');
          setOpen(true);
        };
        timer = setTimeout(showWhenClear, 700);
      })
      .catch(() => {});
    return () => clearTimeout(timer);
  }, [user?.role]);

  // Body scroll lock while open — the modal is blocking, so the page behind it
  // shouldn't move.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const confirm = async () => {
    if (!agreed || saving) return;
    setSaving(true);
    try { await api.post('/editor/song-approval-intro/acknowledge'); } catch { /* close anyway */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    // No onClick on the backdrop: this is deliberately not dismissable.
    <div className="ug-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-labelledby="song-intro-title" data-testid="song-intro-modal">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl">

        {/* Header */}
        <div className="p-6 pb-5 border-b border-ug-border-subtle">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
            <Music className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
          </div>
          <h2 id="song-intro-title" className="font-heading text-2xl text-zinc-100 mb-2">Song Approval</h2>
          <p className="text-[13px] text-zinc-100/75 leading-relaxed">
            Some clients want to okay your song picks before you start editing. You send them
            straight to the client yourself, with no waiting on a project manager, even on a weekend.
            You send song choices once per project, so send your best options.
          </p>
        </div>

        {/* Preview — mirrors the real panel so it can't go stale */}
        <div className="p-6 py-5 space-y-3 bg-ug-surface/30">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">What you send</span>
          <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
            <div className="pb-1 border-b border-ug-border-strong text-[12px] font-semibold text-zinc-100">Intro</div>
            <div className="mt-2.5 space-y-2">
              {/* Licensing sources only. Spotify was a bad example here — you can't
                  license a track from it, so it would never be a real submission. */}
              {['musicbed.com/songs/…', 'artlist.io/song/…'].map((l, i) => (
                <div key={i} className="flex items-center gap-1.5 rounded bg-ug-surface border border-ug-border-strong px-2 py-1.5">
                  <Link2 className="w-3 h-3 text-slate-600 shrink-0" />
                  <span className="text-[11px] text-zinc-100/70 truncate">{l}</span>
                </div>
              ))}
            </div>
            <div className="mt-2.5 flex items-center gap-1 text-[10px] text-oxidized-teal">+ Add another song option to this section</div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <div className="h-px flex-1 bg-ug-border-subtle" />
            <Send className="w-3 h-3 text-ug-gold/60" />
            <div className="h-px flex-1 bg-ug-border-subtle" />
          </div>

          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">What the client does</span>
          <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border space-y-2">
            <div className="space-y-1">
              {[true, false].map((picked, i) => (
                <div key={i} className={`flex items-center gap-2 p-1.5 rounded border ${picked ? 'bg-ug-gold/10 border-ug-gold/30' : 'bg-ug-surface/50 border-ug-border-subtle'}`}>
                  <span className={`w-4 h-4 rounded shrink-0 border flex items-center justify-center ${picked ? 'bg-ug-gold border-ug-gold' : 'border-ug-border-strong'}`}>
                    {picked && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
                  </span>
                  <span className="text-[10px] text-oxidized-teal truncate">{i === 0 ? 'musicbed.com' : 'artlist.io'}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1 text-[10px] text-oxidized-teal">+ Add a link to their own song</div>
            <div className="py-1.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[9px] font-semibold uppercase tracking-wider text-center">Send my song choices</div>
            <p className="text-[10px] text-slate-500 italic">"Use the instrumental version, please"</p>
          </div>
        </div>

        {/* How it works */}
        <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
          {[
            ['Where to find it', 'Open a video project → Notes tab → Song Approval.'],
            ['Add your picks', 'One section per part of the video, or one section with several options to choose from. This is the only set the client gets, so send your best options.'],
            ['You\'ll hear back by email', 'For each section the client keeps your options and can add links to their own song(s). What they send is final: use it as sent. There is no second set; anything more about songs goes to your project manager in chat.'],
          ].map(([t, d], i) => (
            <div key={i} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
              <p className="text-[12px] text-zinc-100/75 leading-relaxed">
                <span className="text-zinc-100 font-medium">{t}.</span> {d}
              </p>
            </div>
          ))}
          <div className="flex gap-3 pt-1">
            <X className="shrink-0 w-4 h-4 text-slate-600 mt-0.5" strokeWidth={2} />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              This isn't a message box: it sends links only. Anything you need to explain goes to your
              project manager in chat, as usual.
            </p>
          </div>
        </div>

        {/* Acknowledge — the only way out */}
        <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 rounded-b-2xl">
          {/* Custom control rather than a native checkbox: the global tap-target
              rule `input[type="checkbox"] { min-width/height: 44px }` (index.css)
              is MORE specific than `.tap-auto`, so a native box can't be opted out
              and renders as a 44px white square. A button also lets it match the
              gold styling used elsewhere. */}
          <button type="button" role="checkbox" aria-checked={agreed}
            onClick={() => setAgreed(v => !v)} data-testid="song-intro-agree"
            className="tap-auto w-full flex items-start gap-2.5 mb-4 text-left group cursor-pointer bg-transparent">
            <span className={`mt-0.5 w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
              agreed ? 'bg-ug-gold border-ug-gold' : 'border-ug-border-strong group-hover:border-ug-gold/50'}`}>
              {agreed && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
            </span>
            <span className="text-[12px] text-zinc-100/80 leading-relaxed group-hover:text-zinc-100 transition-colors">
              I've read this and understand how to send song choices to a client.
            </span>
          </button>
          <button onClick={confirm} disabled={!agreed || saving} data-testid="song-intro-confirm"
            className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}
